import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loadCountries } from 'redux/reducers/countries';
import {
  ICountriesData,
  ITopTerritoriesEntityCountryItem,
} from '../../shared/TerritoryBlock/types';
import { ICountryFlagProps } from 'types/shared';

/**
 * This hook normalizes Short Form Video country data to a usable format.
 * It also adds country metadata to the data.
 *
 * @param {array} initialData An array of the objects of initial data.
 * @returns {array} normalized country data.
 */

interface ICountryData {
  countLastWeek: number;
  countThisWeekToDate: number;
}
interface INormalizedCountry {
  countryId: string;
  data: ICountryData;
}

const useCountryShortFormVideoData = (initialData: ICountriesData[]): any => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadCountries());
  }, [dispatch]);

  const countriesMetadata = useSelector(
    (state) => (state as any).countries.data
  );

  const normalizeCountrySpins = useCallback(
    ({ entity, countries: data }) => {
      const normalizeCountry = ({
        countryId,
        data,
      }: INormalizedCountry): {
        id: string;
        name: string;
        data: ICountryData;
      } => ({
        id: countryId,
        name: countryId,
        data: data,
      });

      const addMetadata = (country: {
        id: string;
        name: string;
        data: ICountryData;
      }): ITopTerritoriesEntityCountryItem => {
        const byCountryId = (countryId: string) => (
          el: ICountryFlagProps
        ): boolean => el.id === countryId;
        return {
          ...country,
          metadata: countriesMetadata.find(byCountryId(country.id)),
        };
      };

      return {
        entity,
        data: data.map(normalizeCountry).map(addMetadata),
      };
    },
    [countriesMetadata]
  );

  const countryRadioSpins = useMemo(() => {
    if (!initialData) return;
    return initialData.map(normalizeCountrySpins);
  }, [initialData, normalizeCountrySpins]);

  return countryRadioSpins;
};

export default useCountryShortFormVideoData;
